import { useState } from "react";
import { ChevronDown } from "lucide-react";
import RichBlock, { Unavailable } from "@/components/vetting/RichBlock";
import { RunsText } from "@/components/appointments/MessageCard";

const plain = (runs) => (runs || []).map((r) => r.t).join("").trim();

// Runs -> array of lines (each a run list), keeping bold/italic/link per run.
export function splitLines(runs) {
  const lines = [[]];
  (runs || []).forEach((r) => r.t.split("\n").forEach((part, i) => {
    if (i > 0) lines.push([]);
    if (part) lines[lines.length - 1].push({ ...r, t: part });
  }));
  return lines;
}

const joinLines = (lines) => lines.flatMap((l, i) => (i ? [{ t: "\n", b: false, i: false, u: null }, ...l] : l));

// Resource column: cards for links; "WATCH VIDEO" text with no link in the sheet shows as not available.
export function ResCol({ sec, testid }) {
  const missing = new Set(sec.missing || []);
  const kept = splitLines(sec.res).filter((l) => !missing.has(l.map((r) => r.t).join("").trim()));
  return (
    <div className="space-y-3">
      {plain(joinLines(kept)) && <RichBlock runs={joinLines(kept)} testid={`${testid}-res`} />}
      {[...missing].map((m, i) => <Unavailable key={i} text={`${m} — link not found in the master sheet`} testid={`${testid}-missing-${i}`} />)}
    </div>
  );
}

export function SectionBody({ sec, testid }) {
  return (
    <div className="space-y-3">
      {plain(sec.body) && <RichBlock runs={sec.body} testid={`${testid}-body`} />}
      {(sec.extra || []).map((e, i) => <RichBlock key={i} runs={e} testid={`${testid}-extra-${i}`} />)}
      {plain(sec.res) || sec.missing?.length ? <ResCol sec={sec} testid={testid} /> : null}
    </div>
  );
}

export function Expandable({ label, children, testid, defaultOpen = false, open: controlled, onToggle, className = "" }) {
  const [own, setOwn] = useState(defaultOpen);
  const open = controlled ?? own;
  const pid = `${testid}-panel`;
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`} data-testid={testid} data-open={open ? "true" : "false"}>
      <button type="button" onClick={onToggle || (() => setOwn((o) => !o))} aria-expanded={open} aria-controls={pid} data-testid={`${testid}-toggle`}
              className="w-full min-h-[48px] flex items-center gap-3 px-4 py-3 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
        <span className="flex-1 text-[15px] font-semibold text-[#0A1F44] leading-snug">{label}</span>
        <ChevronDown aria-hidden="true" className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div id={pid} className="px-4 pb-4 space-y-4" data-testid={pid}>{children}</div>}
    </section>
  );
}

export function Steps({ sec, testid }) {
  const lines = splitLines(sec.body).filter((l) => plain(l));
  return (
    <ol className="space-y-2.5" data-testid={testid}>
      {lines.map((l, i) => {
        const m = plain(l).match(/^(\d+)\.\s*/);
        if (!m) return <li key={i} className="list-none"><RunsText runs={l} /></li>;
        const first = { ...l[0], t: l[0].t.replace(/^\s*\d+\.\s*/, "") };
        return (
          <li key={i} className="flex gap-3 items-start list-none" data-testid={`${testid}-step-${m[1]}`}>
            <span className="w-7 h-7 rounded-full bg-[#0A1F44] text-[#C9A961] text-xs font-semibold flex items-center justify-center shrink-0">{m[1]}</span>
            <RunsText runs={[first, ...l.slice(1)]} className="pt-1" />
          </li>
        );
      })}
    </ol>
  );
}
